import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white py-32" data-testid="privacy-page">
      <div className="max-w-4xl mx-auto px-6">
        <h1 className="text-5xl font-bold mb-4">Privacy Policy</h1>
        <p className="text-sm text-gray-500 mb-2">Last Updated: December 2025</p>
        <p className="text-sm text-gray-600 mb-8">
          <strong>Jurisdiction:</strong> Hyderabad, Telangana, India | 
          <strong> Governing Law:</strong> Digital Personal Data Protection Act, 2023 (DPDP Act)
        </p>
        
        <div className="space-y-8 text-gray-700 leading-relaxed">
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold mb-4 text-black">Introduction</h2>
            <p>
              ZenStudios (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;) is committed to protecting your personal data 
              and respecting your privacy. This Privacy Policy explains how we collect, use, store, and 
              protect your personal data in compliance with the Digital Personal Data Protection Act, 2023 
              (DPDP Act) of India.
            </p>
            <p className="mt-2">
              As a &quot;Data Fiduciary&quot; under the DPDP Act, we are responsible for ensuring that your 
              personal data is processed lawfully, fairly, and transparently.
            </p>
          </section>

          {/* Data We Collect */}
          <section>
            <h2 className="text-2xl font-bold mb-4 text-black">1. Personal Data We Collect</h2>
            <p>We collect the following types of personal data (&quot;Data Principal&quot; information):</p>
            
            <div className="mt-4 space-y-4">
              <div className="bg-gray-50 p-4 rounded">
                <h3 className="font-semibold text-black mb-2">Identity Data</h3>
                <ul className="list-disc ml-6 space-y-1 text-sm">
                  <li>Full name</li>
                  <li>Email address</li>
                  <li>Phone number</li>
                  <li>Profile picture (if provided via Google OAuth)</li>
                </ul>
              </div>
              
              <div className="bg-gray-50 p-4 rounded">
                <h3 className="font-semibold text-black mb-2">Property & Project Data</h3>
                <ul className="list-disc ml-6 space-y-1 text-sm">
                  <li>Property addresses and details</li>
                  <li>Interior design preferences and room configurations</li>
                  <li>Budget and pricing information</li>
                  <li>Property photos you upload</li>
                </ul>
              </div>
              
              <div className="bg-gray-50 p-4 rounded">
                <h3 className="font-semibold text-black mb-2">Technical Data</h3>
                <ul className="list-disc ml-6 space-y-1 text-sm">
                  <li>IP address</li>
                  <li>Browser type and version</li>
                  <li>Device information</li>
                  <li>Usage patterns and page visits</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Purpose of Processing */}
          <section>
            <h2 className="text-2xl font-bold mb-4 text-black">2. Purpose of Data Processing</h2>
            <p>We process your personal data only for specific, lawful purposes:</p>
            <table className="w-full mt-4 border border-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-3 border-b text-sm font-semibold">Purpose</th>
                  <th className="text-left p-3 border-b text-sm font-semibold">Legal Basis</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr>
                  <td className="p-3 border-b">Creating and managing your account</td>
                  <td className="p-3 border-b">Your consent</td>
                </tr>
                <tr>
                  <td className="p-3 border-b">Providing interior design quotes</td>
                  <td className="p-3 border-b">Contract performance</td>
                </tr>
                <tr>
                  <td className="p-3 border-b">Processing property listings</td>
                  <td className="p-3 border-b">Contract performance</td>
                </tr>
                <tr>
                  <td className="p-3 border-b">Sending service-related communications</td>
                  <td className="p-3 border-b">Legitimate interest</td>
                </tr>
                <tr>
                  <td className="p-3 border-b">Marketing communications</td>
                  <td className="p-3 border-b">Your explicit consent</td>
                </tr>
                <tr>
                  <td className="p-3 border-b">Improving our services</td>
                  <td className="p-3 border-b">Legitimate interest</td>
                </tr>
                <tr>
                  <td className="p-3 border-b">Legal compliance</td>
                  <td className="p-3 border-b">Legal obligation</td>
                </tr>
              </tbody>
            </table>
          </section>

          {/* Your Rights */}
          <section>
            <h2 className="text-2xl font-bold mb-4 text-black">3. Your Rights as a Data Principal</h2>
            <p>Under the DPDP Act 2023, you have the following rights:</p>
            
            <div className="mt-4 grid md:grid-cols-2 gap-4">
              <div className="border border-gray-200 p-4 rounded">
                <h3 className="font-semibold text-[#15803D] mb-2">Right to Access</h3>
                <p className="text-sm">Request a summary of your personal data and how it&apos;s being processed.</p>
              </div>
              
              <div className="border border-gray-200 p-4 rounded">
                <h3 className="font-semibold text-[#15803D] mb-2">Right to Correction</h3>
                <p className="text-sm">Request correction of inaccurate or incomplete personal data.</p>
              </div>
              
              <div className="border border-gray-200 p-4 rounded">
                <h3 className="font-semibold text-[#15803D] mb-2">Right to Erasure</h3>
                <p className="text-sm">Request deletion of your personal data when no longer needed.</p>
              </div>
              
              <div className="border border-gray-200 p-4 rounded">
                <h3 className="font-semibold text-[#15803D] mb-2">Right to Withdraw Consent</h3>
                <p className="text-sm">Withdraw your consent at any time with same ease as giving it.</p>
              </div>
              
              <div className="border border-gray-200 p-4 rounded">
                <h3 className="font-semibold text-[#15803D] mb-2">Right to Grievance Redressal</h3>
                <p className="text-sm">Lodge complaints if you believe your data rights are violated.</p>
              </div>
              
              <div className="border border-gray-200 p-4 rounded">
                <h3 className="font-semibold text-[#15803D] mb-2">Right to Data Portability</h3>
                <p className="text-sm">Export your data in a structured, machine-readable format.</p>
              </div>
            </div>
            
            <div className="mt-4 bg-[#15803D]/5 border border-[#15803D]/20 p-4 rounded">
              <p className="text-sm">
                <strong>Exercise Your Rights:</strong> You can exercise these rights through your 
                <Link to="/dashboard" className="text-[#15803D] hover:underline mx-1">Dashboard</Link>
                or by contacting our Data Protection Officer at <strong>dpo@zenstudios.in</strong>
              </p>
            </div>
          </section>

          {/* Data Sharing */}
          <section>
            <h2 className="text-2xl font-bold mb-4 text-black">4. Data Sharing & Third Parties</h2>
            <p>We may share your personal data with:</p>
            <ul className="list-disc ml-6 mt-2 space-y-2">
              <li>
                <strong>Service Partners:</strong> Interior designers, property agents, and developers 
                (only when you request their services)
              </li>
              <li>
                <strong>Technology Providers:</strong> Cloud hosting (secure, encrypted), payment 
                processors (PCI-DSS compliant)
              </li>
              <li>
                <strong>Legal Authorities:</strong> When required by law or court order
              </li>
            </ul>
            <p className="mt-4 font-semibold text-[#15803D]">
              We do NOT sell your personal data to third parties.
            </p>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-2xl font-bold mb-4 text-black">5. Data Retention</h2>
            <p>We retain your personal data only as long as necessary:</p>
            <table className="w-full mt-4 border border-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-3 border-b">Data Type</th>
                  <th className="text-left p-3 border-b">Retention Period</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="p-3 border-b">Account data</td>
                  <td className="p-3 border-b">Until account deletion + 30 days</td>
                </tr>
                <tr>
                  <td className="p-3 border-b">Transaction records</td>
                  <td className="p-3 border-b">7 years (legal requirement)</td>
                </tr>
                <tr>
                  <td className="p-3 border-b">Design quotes</td>
                  <td className="p-3 border-b">3 years or until project completion</td>
                </tr>
                <tr>
                  <td className="p-3 border-b">Marketing preferences</td>
                  <td className="p-3 border-b">Until consent withdrawn</td>
                </tr>
                <tr>
                  <td className="p-3 border-b">Technical logs</td>
                  <td className="p-3 border-b">90 days</td>
                </tr>
              </tbody>
            </table>
          </section>

          {/* Data Security */}
          <section>
            <h2 className="text-2xl font-bold mb-4 text-black">6. Data Security</h2>
            <p>We implement reasonable security safeguards as required by the DPDP Act:</p>
            <ul className="list-disc ml-6 mt-2 space-y-2">
              <li><strong>Encryption in Transit:</strong> TLS 1.2+ for all data transmission</li>
              <li><strong>Encryption at Rest:</strong> PII encrypted in our databases</li>
              <li><strong>Access Controls:</strong> Role-based access, principle of least privilege</li>
              <li><strong>Authentication:</strong> Secure password hashing (bcrypt), JWT tokens</li>
              <li><strong>Monitoring:</strong> Security audit logs, intrusion detection</li>
            </ul>
          </section>

          {/* Cookies */}
          <section>
            <h2 className="text-2xl font-bold mb-4 text-black">7. Cookies & Tracking</h2>
            <p>We use cookies with your consent for:</p>
            <ul className="list-disc ml-6 mt-2 space-y-2">
              <li><strong>Necessary:</strong> Authentication, security, session management</li>
              <li><strong>Functional:</strong> Remembering your preferences (city, theme)</li>
              <li><strong>Analytics:</strong> Understanding usage patterns (with consent)</li>
              <li><strong>Marketing:</strong> Personalized content (with explicit consent)</li>
            </ul>
            <p className="mt-2">
              You can manage your cookie preferences at any time through our cookie settings banner.
            </p>
          </section>

          {/* Data Breach */}
          <section>
            <h2 className="text-2xl font-bold mb-4 text-black">8. Data Breach Notification</h2>
            <p>
              In the event of a personal data breach that may cause harm to you, we will:
            </p>
            <ul className="list-disc ml-6 mt-2 space-y-2">
              <li>Notify the Data Protection Board of India within <strong>72 hours</strong></li>
              <li>Inform affected Data Principals without undue delay</li>
              <li>Provide details of the breach and remedial measures taken</li>
            </ul>
          </section>

          {/* Children&apos;s Privacy */}
          <section>
            <h2 className="text-2xl font-bold mb-4 text-black">9. Children&apos;s Privacy</h2>
            <p>
              Our services are not intended for individuals under 18 years of age. We do not 
              knowingly collect personal data from children. If you are a parent or guardian 
              and believe your child has provided us with personal data, please contact us 
              immediately, and we will delete such information.
            </p>
          </section>

          {/* Grievance Officer */}
          <section>
            <h2 className="text-2xl font-bold mb-4 text-black">10. Grievance Redressal</h2>
            <p>
              If you have any concerns about how your personal data is being processed, you may 
              contact our Grievance Officer:
            </p>
            <div className="mt-4 bg-gray-50 p-4 rounded">
              <p className="font-semibold">Data Protection Officer / Grievance Officer</p>
              <p className="mt-2 text-sm">
                <strong>Email:</strong> dpo@zenstudios.in<br />
                <strong>Phone:</strong> +91 40 1234 5678<br />
                <strong>Address:</strong> ZenStudios, Hitech City, Hyderabad, Telangana 500081, India
              </p>
              <p className="mt-2 text-sm">
                We will acknowledge your grievance within <strong>48 hours</strong> and resolve it 
                within <strong>30 days</strong>.
              </p>
            </div>
            <p className="mt-4 text-sm">
              If you are not satisfied with our response, you may escalate your complaint to the 
              <strong> Data Protection Board of India</strong>.
            </p>
          </section>

          {/* Updates */}
          <section>
            <h2 className="text-2xl font-bold mb-4 text-black">11. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy to reflect changes in our practices or legal 
              requirements. We will notify you of significant changes via email or a prominent 
              notice on our website. Your continued use of our services after such notification 
              constitutes acceptance of the updated policy.
            </p>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-2xl font-bold mb-4 text-black">12. Contact Us</h2>
            <p>For any questions about this Privacy Policy or our data practices:</p>
            <div className="mt-4 bg-[#15803D]/5 p-4 rounded border border-[#15803D]/20">
              <p className="font-semibold text-[#15803D]">ZenStudios</p>
              <p className="mt-2 text-sm">
                <strong>Email:</strong> privacy@zenstudios.in<br />
                <strong>DPO:</strong> dpo@zenstudios.in<br />
                <strong>Phone:</strong> +91 40 1234 5678<br />
                <strong>Address:</strong> Hitech City, Hyderabad, Telangana 500081, India
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
